import React from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { es } from 'date-fns/locale';
import { CalendarX2 } from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';
import { isDateInWeek } from '../../utils/dateUtils';
import './WeekHighlightDatePicker.css';

// Register Spanish (Chile) locale
registerLocale('es', es);

/**
 * WeekHighlightDatePicker - Custom date picker with week highlighting
 * 
 * Highlights the current season/week range to provide visual feedback
 * when selecting dates in Preparativos steps.
 * 
 * @param {Object} props
 * @param {string} props.value - Selected date in YYYY-MM-DD format
 * @param {Function} props.onChange - Callback when date changes (receives YYYY-MM-DD string)
 * @param {number} props.season - Current season/year (e.g., 2026)
 * @param {number} props.week - Current ISO week number (1-53)
 * @param {Date} props.minDate - Minimum selectable date (optional)
 * @param {Date} props.maxDate - Maximum selectable date (optional)
 * @param {string} props.placeholder - Placeholder text (optional)
 */
const WeekHighlightDatePicker = ({ 
  value, 
  onChange, 
  season, 
  week, 
  minDate, 
  maxDate,
  placeholder = 'Seleccionar fecha',
  disabled = false
}) => {
  // Convert YYYY-MM-DD string to Date object for DatePicker
  const selectedDate = value ? new Date(value + 'T00:00:00') : null;

  // Handle date selection
  const handleChange = (date) => {
    if (date) {
      // Convert Date to YYYY-MM-DD format
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateString = `${year}-${month}-${day}`;
      onChange(dateString);
    } else {
      onChange('');
    }
  };

  // Custom day class name function to highlight week dates
  const getDayClassName = (date) => {
    if (!season || !week) return '';
    
    // Check if this date is in the current week
    if (isDateInWeek(date, season, week)) {
      return 'highlighted-week-day';
    }
    
    return '';
  };

  // Custom clear button with CalendarX2 icon
  const CustomClearButton = ({ onClick }) => (
    <button
      type="button"
      className="custom-clear-button"
      onClick={onClick}
      aria-label="Limpiar fecha"
    >
      <CalendarX2 size={16} />
    </button>
  );

  return (
    <div className="week-highlight-datepicker-wrapper">
      <DatePicker
        selected={selectedDate}
        onChange={handleChange}
        dateFormat="dd/MM/yyyy"
        locale="es"
        placeholderText={placeholder}
        minDate={minDate}
        maxDate={maxDate}
        dayClassName={getDayClassName}
        showPopperArrow={false}
        className="step-input"
        calendarClassName="week-highlight-calendar"
        isClearable={false}
        showYearDropdown
        showMonthDropdown
        dropdownMode="select"
        popperPlacement="bottom-start"
        disabled={disabled}
      />
      {selectedDate && !disabled && (
        <CustomClearButton onClick={() => handleChange(null)} />
      )}
    </div>
  );
};

export default WeekHighlightDatePicker;
